import { useEffect, useRef, useState } from 'react';
import { Icon } from '../components/ui/Icon';
import { useT } from '../i18n';
import { normalizeAccessLevel, type AccessLevel } from './types';

const LEVELS: readonly AccessLevel[] = ['view', 'edit_data', 'edit_format', 'edit_formula'];

function LevelIcon({ level }: { level: AccessLevel }) {
  return <Icon name={level === 'view' ? 'eye' : 'pencil'} className="h-4 w-4 shrink-0" />;
}

interface Props {
  value: AccessLevel;
  onChange: (level: AccessLevel) => void;
  /** Renders as static text — used for access inherited from the org role. */
  readOnly?: boolean;
  /** Accessible name, e.g. the person the level applies to. */
  label: string;
  className?: string;
}

/**
 * View/Edit picker. A native <select> cannot render icons in its options, which
 * is why TMS hand-rolled this one (`SelectorNivel`) and why it survives the
 * port. Keyboard behaviour is the part that had to be added: the TMS version
 * closed on outside click only.
 */
export function AccessLevelSelect({
  value,
  onChange,
  readOnly = false,
  label,
  className = '',
}: Props) {
  const t = useT();
  const effectiveValue = normalizeAccessLevel(String(value));
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Only this popover closes; the dialog behind it stays open.
        event.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  if (readOnly) {
    return (
      <span className={`flex items-center gap-1.5 text-sm text-muted ${className}`}>
        <LevelIcon level={effectiveValue} />
        {t(`share.level.${effectiveValue}`)}
      </span>
    );
  }

  return (
    <div ref={ref} className={`relative ${open ? 'z-20' : ''} ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((previous) => !previous)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('share.levelFor', { name: label })}
        className="tap-target flex w-full items-center gap-1.5 rounded-full border border-border-strong bg-surface px-2.5 text-sm text-fg hover:border-accent"
      >
        <LevelIcon level={effectiveValue} />
        <span className="truncate">{t(`share.level.${effectiveValue}`)}</span>
        <Icon name="chevronDown" className="ml-auto h-3 w-3 shrink-0 text-muted" />
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute right-0 top-[calc(100%+4px)] z-30 w-52 max-w-[calc(100vw-2rem)] rounded-token border border-border bg-surface py-1 shadow-[0_4px_16px_rgb(15_23_42_/_0.08)]"
        >
          {LEVELS.map((level) => (
            <li key={level} role="option" aria-selected={level === value}>
              <button
                type="button"
                onClick={() => {
                  onChange(level);
                  setOpen(false);
                }}
                className={[
                  'tap-target flex w-full items-center gap-2 px-3 text-left text-sm',
                  'hover:bg-surface-3',
                  level === effectiveValue ? 'text-accent' : 'text-fg',
                ].join(' ')}
              >
                <LevelIcon level={level} />
                {t(`share.level.${level}`)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
